export interface CropAreaPixels {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CropViewportState {
  position: { x: number; y: number };
  zoom: number;
}

export interface ImageCropPreset {
  aspectRatio: number;
  outputWidth: number;
  outputHeight: number;
  hint: string;
  /** Optional fixed crop UI size. When set, modal preview uses these pixels. */
  viewportWidth?: number;
  viewportHeight?: number;
  centerWhenContained?: boolean;
  /**
   * Initial fit when no persisted/derived crop state is available.
   * `cover` matches CSS `background-size: cover` / `object-fit: cover` (centered).
   */
  defaultFit?: "contain" | "cover";
}

/**
 * Widest embed / multipage content column (90% shell = 66rem @ 16px).
 * Banner aspect ratio is derived from this width and the 450px height cap
 * so the full image fills edge-to-edge at max width without display cropping.
 */
export const BANNER_MAX_CONTENT_WIDTH_PX = 1056;
export const BANNER_DISPLAY_MAX_HEIGHT_PX = 450;

/** 1056∶450 — matches max shell width at 450px tall (not 16∶9). */
export const BANNER_ASPECT_RATIO =
  BANNER_MAX_CONTENT_WIDTH_PX / BANNER_DISPLAY_MAX_HEIGHT_PX;

export const BANNER_EXPORT_WIDTH = 2112;
export const BANNER_EXPORT_HEIGHT = Math.round(
  BANNER_EXPORT_WIDTH / BANNER_ASPECT_RATIO
);

export const BANNER_MIN_EXPORT_WIDTH = 1600;
export const BANNER_MIN_EXPORT_HEIGHT = Math.round(
  BANNER_MIN_EXPORT_WIDTH / BANNER_ASPECT_RATIO
);

export const BANNER_CROP: ImageCropPreset = {
  aspectRatio: BANNER_ASPECT_RATIO,
  outputWidth: BANNER_EXPORT_WIDTH,
  outputHeight: BANNER_EXPORT_HEIGHT,
  centerWhenContained: true,
  hint: `Drag to reposition · ${BANNER_MAX_CONTENT_WIDTH_PX}×${BANNER_DISPLAY_MAX_HEIGHT_PX} display · exports ${BANNER_EXPORT_WIDTH}×${BANNER_EXPORT_HEIGHT}`,
};

export const HIGHLIGHT_CROP: ImageCropPreset = {
  aspectRatio: 1,
  outputWidth: 1200,
  outputHeight: 1200,
  centerWhenContained: true,
  hint: "Drag to reposition · 1:1 square · 1200×1200",
};

/** Profile avatar — 1:1 crop shown as a circle in the editor and on the page. */
export const PROFILE_CROP: ImageCropPreset = {
  aspectRatio: 1,
  outputWidth: 1200,
  outputHeight: 1200,
  centerWhenContained: true,
  hint: "Drag to reposition · circular profile · 1200×1200",
};

/**
 * Links grid card image — matches --content-square-size × --grid-image-height
 * (143×92) used by GridTemplate.
 */
export const LINK_GRID_ASPECT_RATIO = 143 / 92;
export const LINK_GRID_CROP: ImageCropPreset = {
  aspectRatio: LINK_GRID_ASPECT_RATIO,
  outputWidth: 1200,
  outputHeight: Math.round(1200 / LINK_GRID_ASPECT_RATIO),
  // No defaultFit here — fresh uploads must open contain @ zoom 1
  // (ImageCropperModal would otherwise cover-fit and look like "random" zoom).
  // Edit-restore cover falls back via useImageCropper({ defaultFit: "cover" }).
  // Min zoom = contain (longest edge touches the frame). Free pan lets
  // letterbox padding sit on either side of the shorter axis.
  centerWhenContained: false,
  hint: "Drag to reposition · grid link image · 143∶92",
};

/**
 * Legacy LiB line-template link thumb — reuses profile export size; free
 * letterbox pan (profile avatar crop stays `centerWhenContained: true`).
 */
export const LINK_LINE_CROP: ImageCropPreset = {
  ...PROFILE_CROP,
  centerWhenContained: false,
  hint: "Drag to reposition · line link image · 1∶1",
};

/**
 * Collection post card crop — 215×215 square tile in the collection editor.
 */
export const COLLECTION_CARD_WIDTH = 215;
export const COLLECTION_CARD_HEIGHT = 215;
export const COLLECTION_CARD_ASPECT_RATIO = 1;
export const COLLECTION_CARD_CROP: ImageCropPreset = {
  aspectRatio: COLLECTION_CARD_ASPECT_RATIO,
  outputWidth: 1200,
  outputHeight: 1200,
  viewportWidth: COLLECTION_CARD_WIDTH,
  viewportHeight: COLLECTION_CARD_HEIGHT,
  defaultFit: "cover",
  centerWhenContained: true,
  hint: "Drag to reposition · collection card · 215∶215",
};

/**
 * Product Bank card — matches Link-in-Bio rectangle
 * (--content-rectangle-width × --content-rectangle-height = 143×250).
 */
export const PRODUCT_BANK_WIDTH = 143;
export const PRODUCT_BANK_HEIGHT = 250;
export const PRODUCT_BANK_ASPECT_RATIO =
  PRODUCT_BANK_WIDTH / PRODUCT_BANK_HEIGHT;
export const PRODUCT_BANK_CROP: ImageCropPreset = {
  aspectRatio: PRODUCT_BANK_ASPECT_RATIO,
  outputWidth: PRODUCT_BANK_WIDTH * 8,
  outputHeight: PRODUCT_BANK_HEIGHT * 8,
  viewportWidth: PRODUCT_BANK_WIDTH,
  viewportHeight: PRODUCT_BANK_HEIGHT,
  centerWhenContained: true,
  hint: "Drag to reposition · product card · 143∶250",
};

/** Custom social icon — 1:1 crop shown circular alongside platform icons. */
export const SOCIAL_ICON_VIEWPORT = 320;
export const SOCIAL_ICON_CROP: ImageCropPreset = {
  aspectRatio: 1,
  outputWidth: 256,
  outputHeight: 256,
  viewportWidth: SOCIAL_ICON_VIEWPORT,
  viewportHeight: SOCIAL_ICON_VIEWPORT,
  centerWhenContained: true,
  hint: "Drag to reposition · square icon · 256×256",
};

/** Blog dashboard cover — portrait 4∶5 (matches public journal cards / mockup). */
export const BLOG_COVER_ASPECT_RATIO = 4 / 5;
export const BLOG_COVER_VIEWPORT_WIDTH = 280;
export const BLOG_COVER_VIEWPORT_HEIGHT = 350;
export const BLOG_COVER_CROP: ImageCropPreset = {
  aspectRatio: BLOG_COVER_ASPECT_RATIO,
  outputWidth: 1200,
  outputHeight: 1500,
  viewportWidth: BLOG_COVER_VIEWPORT_WIDTH,
  viewportHeight: BLOG_COVER_VIEWPORT_HEIGHT,
  centerWhenContained: true,
  defaultFit: "cover",
  hint: "Drag to reposition · Crops to 4:5",
};

/** @deprecated Use BANNER_EXPORT_WIDTH */
export const BANNER_IMAGE_WIDTH = BANNER_EXPORT_WIDTH;
/** @deprecated Use BANNER_EXPORT_HEIGHT */
export const BANNER_IMAGE_HEIGHT = BANNER_EXPORT_HEIGHT;
export const BANNER_DISPLAY_HEIGHT_PX = BANNER_DISPLAY_MAX_HEIGHT_PX;

const CROP_VIEWPORT_MIN_HEIGHT = 120;
const CROP_VIEWPORT_DEFAULT_WIDTH = 320;
const CROP_VIEWPORT_MAX_WIDTH = 640;

export function getCropViewportDimensions(
  aspectRatio: number,
  fixedViewport?: { width?: number; height?: number } | null
) {
  if (
    fixedViewport?.width &&
    fixedViewport?.height &&
    fixedViewport.width > 0 &&
    fixedViewport.height > 0
  ) {
    return {
      viewportWidth: Math.round(fixedViewport.width),
      viewportHeight: Math.round(fixedViewport.height),
    };
  }

  let viewportWidth = CROP_VIEWPORT_DEFAULT_WIDTH;
  let viewportHeight = Math.round(viewportWidth / aspectRatio);

  if (viewportHeight < CROP_VIEWPORT_MIN_HEIGHT) {
    viewportHeight = CROP_VIEWPORT_MIN_HEIGHT;
    viewportWidth = Math.round(viewportHeight * aspectRatio);
  }

  viewportWidth = Math.min(viewportWidth, CROP_VIEWPORT_MAX_WIDTH);
  viewportHeight = Math.round(viewportWidth / aspectRatio);

  return { viewportWidth, viewportHeight };
}

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Failed to load image"));
    image.src = src;
  });
}

export const IMAGE_CROP_MIN_ZOOM = 1;
/** Default slider ceiling when the image already covers the frame at 1×. */
export const IMAGE_CROP_BASE_MAX_ZOOM = 3;
/** Hard cap so extremely tall/narrow images cannot zoom arbitrarily. */
export const IMAGE_CROP_ABSOLUTE_MAX_ZOOM = 8;
/** Extra zoom past “cover” so users can still pan into a tighter crop. */
const COVER_ZOOM_HEADROOM = 1.5;

export function getContainScale(
  naturalWidth: number,
  naturalHeight: number,
  viewportWidth: number,
  viewportHeight: number
) {
  return Math.min(
    viewportWidth / naturalWidth,
    viewportHeight / naturalHeight
  );
}

export function getCoverScale(
  naturalWidth: number,
  naturalHeight: number,
  viewportWidth: number,
  viewportHeight: number
) {
  return Math.max(
    viewportWidth / naturalWidth,
    viewportHeight / naturalHeight
  );
}

/**
 * Max zoom relative to contain-fit. High enough to fill the crop viewport
 * when desired, with headroom for tighter framing.
 */
export function getMaxZoomForImage(
  naturalWidth: number,
  naturalHeight: number,
  viewportWidth: number,
  viewportHeight: number
) {
  if (!naturalWidth || !naturalHeight) {
    return IMAGE_CROP_BASE_MAX_ZOOM;
  }

  const containScale = getContainScale(
    naturalWidth,
    naturalHeight,
    viewportWidth,
    viewportHeight
  );
  const coverScale = getCoverScale(
    naturalWidth,
    naturalHeight,
    viewportWidth,
    viewportHeight
  );
  const zoomToCover = coverScale / containScale;

  return Math.min(
    IMAGE_CROP_ABSOLUTE_MAX_ZOOM,
    Math.max(IMAGE_CROP_BASE_MAX_ZOOM, zoomToCover * COVER_ZOOM_HEADROOM)
  );
}

export function clampCropPosition(
  x: number,
  y: number,
  scale: number,
  naturalWidth: number,
  naturalHeight: number,
  viewportWidth: number,
  viewportHeight: number,
  centerWhenContained = false
) {
  const displayWidth = naturalWidth * scale;
  const displayHeight = naturalHeight * scale;

  let minX: number;
  let maxX: number;
  if (displayWidth <= viewportWidth) {
    if (centerWhenContained) {
      minX = maxX = (viewportWidth - displayWidth) / 2;
    } else {
      // Letterbox: slide within the frame so padding can sit on either side.
      minX = 0;
      maxX = viewportWidth - displayWidth;
    }
  } else {
    minX = viewportWidth - displayWidth;
    maxX = 0;
  }

  let minY: number;
  let maxY: number;
  if (displayHeight <= viewportHeight) {
    if (centerWhenContained) {
      minY = maxY = (viewportHeight - displayHeight) / 2;
    } else {
      minY = 0;
      maxY = viewportHeight - displayHeight;
    }
  } else {
    minY = viewportHeight - displayHeight;
    maxY = 0;
  }

  return {
    x: Math.min(maxX, Math.max(minX, x)),
    y: Math.min(maxY, Math.max(minY, y)),
  };
}

/**
 * Zoom/pan that matches CSS `background-size: cover` + `background-position: center`
 * (or `object-fit: cover`) inside the crop viewport.
 */
export function getCoverAlignedCropViewportState(
  naturalWidth: number,
  naturalHeight: number,
  viewportWidth: number,
  viewportHeight: number
): CropViewportState {
  const containScale = getContainScale(
    naturalWidth,
    naturalHeight,
    viewportWidth,
    viewportHeight
  );
  const coverScale = getCoverScale(
    naturalWidth,
    naturalHeight,
    viewportWidth,
    viewportHeight
  );
  const zoom = Math.max(
    IMAGE_CROP_MIN_ZOOM,
    containScale > 0 ? coverScale / containScale : IMAGE_CROP_MIN_ZOOM
  );
  const effectiveScale = containScale * zoom;
  return {
    zoom,
    position: clampCropPosition(
      (viewportWidth - naturalWidth * effectiveScale) / 2,
      (viewportHeight - naturalHeight * effectiveScale) / 2,
      effectiveScale,
      naturalWidth,
      naturalHeight,
      viewportWidth,
      viewportHeight,
      true
    ),
  };
}

/** Load an image URL and return cover-aligned crop state for a viewport. */
export function deriveCoverAlignedCropStateFromImageUrl(
  imageUrl: string,
  viewportWidth: number,
  viewportHeight: number
): Promise<CropViewportState> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve(
        getCoverAlignedCropViewportState(
          img.naturalWidth,
          img.naturalHeight,
          viewportWidth,
          viewportHeight
        )
      );
    };
    img.onerror = () => reject(new Error("Failed to load image for cover crop"));
    img.src = imageUrl;
  });
}

export function getCropAreaFromViewport(
  position: { x: number; y: number },
  scale: number,
  viewportWidth: number,
  viewportHeight: number
): CropAreaPixels {
  return {
    x: -position.x / scale,
    y: -position.y / scale,
    width: viewportWidth / scale,
    height: viewportHeight / scale,
  };
}

export interface ClippedCropDrawParams {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
  dx: number;
  dy: number;
  dw: number;
  dh: number;
}

/**
 * Map a crop window to in-bounds `drawImage` args.
 * Safari clamps negative source coords to 0 without shifting dest (top-left);
 * clipping here keeps letterboxed / centered crops centered in every browser.
 * Fully in-bounds crops keep dest at (0,0) and the original source rect.
 */
export function getClippedCropDrawParams(
  cropArea: CropAreaPixels,
  imageWidth: number,
  imageHeight: number,
  outputWidth: number,
  outputHeight: number
): ClippedCropDrawParams | null {
  if (
    !(
      cropArea.width > 0 &&
      cropArea.height > 0 &&
      imageWidth > 0 &&
      imageHeight > 0 &&
      outputWidth > 0 &&
      outputHeight > 0
    )
  ) {
    return null;
  }

  const ix1 = Math.max(cropArea.x, 0);
  const iy1 = Math.max(cropArea.y, 0);
  const ix2 = Math.min(cropArea.x + cropArea.width, imageWidth);
  const iy2 = Math.min(cropArea.y + cropArea.height, imageHeight);
  if (!(ix2 > ix1 && iy2 > iy1)) {
    return null;
  }

  const sw = ix2 - ix1;
  const sh = iy2 - iy1;
  return {
    sx: ix1,
    sy: iy1,
    sw,
    sh,
    dx: ((ix1 - cropArea.x) / cropArea.width) * outputWidth,
    dy: ((iy1 - cropArea.y) / cropArea.height) * outputHeight,
    dw: (sw / cropArea.width) * outputWidth,
    dh: (sh / cropArea.height) * outputHeight,
  };
}

export async function cropImageToFile(
  imageSrc: string,
  cropArea: CropAreaPixels,
  outputWidth: number,
  outputHeight: number,
  fileName: string
): Promise<File> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = outputWidth;
  canvas.height = outputHeight;

  const ctx = canvas.getContext("2d", { alpha: true });
  if (!ctx) {
    throw new Error("Unable to crop image");
  }

  ctx.clearRect(0, 0, outputWidth, outputHeight);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  const draw = getClippedCropDrawParams(
    cropArea,
    image.naturalWidth,
    image.naturalHeight,
    outputWidth,
    outputHeight
  );
  if (draw) {
    ctx.drawImage(
      image,
      draw.sx,
      draw.sy,
      draw.sw,
      draw.sh,
      draw.dx,
      draw.dy,
      draw.dw,
      draw.dh
    );
  }

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (result) resolve(result);
        else reject(new Error("Failed to export cropped image"));
      },
      "image/png"
    );
  });

  return new File([blob], fileName, { type: "image/png" });
}
